// ** React Imports
import { createContext, useMemo, useState } from 'react'

// ** Config
import { audiencesUrl, audienceImportUrl, filterUrl, getTags } from 'src/configs/api'
import { useRouter } from 'next/router'
import * as yup from 'yup'
import PropTypes from 'prop-types'
import Icon from 'src/@core/components/icon'
import { Box } from '@mui/material'
import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

const MAX_IMPORT_COLUMNS = 25

const defaultProvider = {
  selectedAudienceEvents: [],
  setSelectedAudienceEvents: () => null,
  csvData: '',
  setCsvData: () => null,
  csvDataStatus: '',
  setCsvDataStatus: () => null,
  importMethod: '',
  setImportMethod: () => null,
  deleteId: 0,
  setDeleteId: () => null,
  deleteAudienceName: '',
  setDeleteAudienceName: () => null,
  openDelete: false,
  setOpenDelete: () => null,
  selectedColumns: [],
  setSelectedColumns: () => null,
  files: [],
  setFiles: () => null,
  mappedColumns: [],
  setMappedColumns: () => null,
  systemColumns: [],
  setSystemColumns: () => null,
  selectedTags: [],
  setSelectedTags: () => null,
  autoFileTags: [],
  setAutoFileTags: () => null,
  tagsData: [],
  setTagsData: () => null,
  rawFileHeadersData: {},
  setRawFileHeadersData: () => null,
  updatedRawFileHeadersData: {},
  setUpdatedRawFileHeadersData: () => null,
  previewData: {},
  setPreviewData: () => null,
  page: 0,
  setPage: () => null,
  rowsPerPage: 25,
  setRowsPerPage: () => null,
  searchTerm: '',
  setSearchTerm: () => null,
  eventSearchTerm: '',
  setEventSearchTerm: () => null,
  rows: [],
  setRows: () => null,
  selectedRows: [],
  setSelectedRows: () => null,
  rowCount: 0,
  setRowCount: () => null,
  eventRowCount: 0,
  setEventRowCount: () => null,
  sortOrder: 'desc',
  setSortOrder: () => null,
  sortOrderBy: '',
  setSortOrderBy: () => null,
  audiencesListRow: [],
  setAudiencesListRow: () => null,
  selectedAudience: {},
  setSelectedAudience: () => null,
  selectedAudienceID: '',
  setSelectedAudienceID: () => null,
  systemColumnList: [],
  setSystemColumnList: () => null,
  columnStatusList: [],
  setColumnStatusList: () => null,
  dataStatus: true,
  setDataStatus: () => null,
  maskField: { email: true, phone: true },
  setMaskField: () => null,
  clicked: false,
  setClicked: () => null,
  isUniqueColumnExists: false,
  setIsUniqueColumnExists: () => null,
  piiColumns: [],
  setPiiColumns: () => null,
  openConfirmPii: false,
  setOpenConfirmPii: () => null,
  isPiiChanged: false,
  setIsPiiChanged: () => null,
  listAudience: () => Promise.resolve(),
  audienceFilterList: [],
  setAudienceFilterList: () => null,
  audienceOtherFilterList: [],
  setAudienceOtherFilterList: () => null,
  hasTagFilter: false,
  setHasTagFilter: () => null,
  isNewFileUpdloaded: false,
  setIsNewFileUpdloaded: () => null,
  createSegment: true,
  setCreateSegment: () => null
}

const AudienceContext = createContext(defaultProvider)

const AudienceProvider = ({ children }) => {
  const [audiencesListRow, setAudiencesListRow] = useState(defaultProvider.audiencesListRow)

  const [selectedAudience, setSelectedAudience] = useState(defaultProvider.selectedAudience)

  const [selectedAudienceID, setSelectedAudienceID] = useState(defaultProvider.selectedAudienceID)

  const [page, setPage] = useState(defaultProvider.page)

  const [rowsPerPage, setRowsPerPage] = useState(defaultProvider.rowsPerPage)

  const [searchTerm, setSearchTerm] = useState(defaultProvider.searchTerm)

  const [eventSearchTerm, setEventSearchTerm] = useState(defaultProvider.eventSearchTerm)

  const [rows, setRows] = useState(defaultProvider.rows)

  const [selectedRows, setSelectedRows] = useState(defaultProvider.selectedRows)

  const [sortOrder, setSortOrder] = useState(defaultProvider.sortOrder)

  const [sortOrderBy, setSortOrderBy] = useState(defaultProvider.sortOrderBy)

  const [rowCount, setRowCount] = useState(defaultProvider.rowCount)

  const [eventRowCount, setEventRowCount] = useState(defaultProvider.eventRowCount)

  const [files, setFiles] = useState(defaultProvider.files)

  const [rawFileHeadersData, setRawFileHeadersData] = useState(defaultProvider.rawFileHeadersData)

  const [mappedColumns, setMappedColumns] = useState(defaultProvider.mappedColumns)

  const [systemColumns, setSystemColumns] = useState(defaultProvider.systemColumns)

  const [selectedTags, setSelectedTags] = useState(defaultProvider.selectedTags)

  const [autoFileTags, setAutoFileTags] = useState(defaultProvider.autoFileTags)

  const [tagsData, setTagsData] = useState(defaultProvider.tagsData)

  const [systemColumnList, setSystemColumnList] = useState(defaultProvider.systemColumnList)

  const [columnStatusList, setColumnStatusList] = useState(defaultProvider.columnStatusList)

  const [dataStatus, setDataStatus] = useState(defaultProvider.dataStatus)

  const [previewData, setPreviewData] = useState(defaultProvider.previewData)

  const [updatedRawFileHeadersData, setUpdatedRawFileHeadersData] = useState(defaultProvider.updatedRawFileHeadersData)

  const [maskField, setMaskField] = useState(defaultProvider.maskField)

  const [isUniqueColumnExists, setIsUniqueColumnExists] = useState(defaultProvider.isUniqueColumnExists)

  const [selectedColumns, setSelectedColumns] = useState(defaultProvider.selectedColumns)

  const [openDelete, setOpenDelete] = useState(defaultProvider.openDelete)

  const [deleteAudienceName, setDeleteAudienceName] = useState(defaultProvider.deleteAudienceName)

  const [deleteId, setDeleteId] = useState(defaultProvider.deleteId)

  const [piiColumns, setPiiColumns] = useState(defaultProvider.piiColumns)

  const [openConfirmPii, setOpenConfirmPii] = useState(defaultProvider.openConfirmPii)

  const [isPiiChanged, setIsPiiChanged] = useState(defaultProvider.isPiiChanged)

  const [importMethod, setImportMethod] = useState(defaultProvider.importMethod)

  const [csvData, setCsvData] = useState(defaultProvider.csvData)

  const [selectedAudienceEvents, setSelectedAudienceEvents] = useState(defaultProvider.selectedAudienceEvents)
  const [audienceFilterList, setAudienceFilterList] = useState(defaultProvider.audienceFilterList)
  const [audienceOtherFilterList, setAudienceOtherFilterList] = useState(defaultProvider.audienceOtherFilterList)
  const [hasTagFilter, setHasTagFilter] = useState(defaultProvider.hasTagFilter)
  const [isNewFileUploaded, setIsNewFileUploaded] = useState(defaultProvider.isNewFileUploaded)
  const [createSegment, setCreateSegment] = useState(defaultProvider.createSegment)
  const { showSuccess, showError } = useToast()

  const mandatoryColumnsToImport = ['EmailAddress', 'PhoneNumber', 'ExternalId', 'Email', 'Phone']

  const schema = yup.object().shape({
    email: yup
      .string()
      .test(
        'is-valid-email',
        'Invalid email format',
        value => !value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
      )
      .nullable(),
    firstName: yup
      .string()
      .max(100, 'First Name must be at most 100 characters long')
      .matches(/^[A-Za-z\s]*$/, 'First Name should contain only alphabets and spaces')
      .nullable(),
    lastName: yup
      .string()
      .max(100, 'Last Name must be at most 100 characters long')
      .matches(/^[A-Za-z\s]*$/, 'Last Name should contain only alphabets and spaces')
      .nullable(),
    phone: yup
      .string()
      .test(
        'is-valid-phone',
        'Phone number must be 10-15 characters and include only digits and symbols like +, -, (, )',
        value => !value || /^[0-9()+-]{10,15}$/.test(value)
      )
      .nullable(),
    addressLine1: yup.string().max(200, 'Address Line 1 must be at most 200 characters long').nullable(),
    addressLine2: yup.string().max(200, 'Address Line 2 must be at most 200 characters long').nullable(),
    city: yup.string().max(100, 'City must be at most 100 characters long').nullable(),
    zip: yup
      .string()
      .max(25, 'ZIP code must be at most 25 characters long')
      .matches(/^[0-9]*$/, 'ZIP code should contain only numbers')
      .nullable(),
    state: yup.string().max(100, 'State must be at most 100 characters long').nullable(),
    country: yup.string().max(255, 'Country must be at most 255 characters long').nullable(),
    birthday: yup.string().max(255, 'Birthday must be at most 255 characters long').nullable(),
    age: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' ? null : value
      })
      .positive('Age must be a positive number')
      .integer('Age must be an integer')
      .nullable(),
    tags: yup.array().of(yup.string().max(255, 'Each tag must be at most 255 characters long'))
  })

  const defaultValues = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    sex: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    birthday: '',
    age: null
  }

  const router = useRouter()

  const api = useAPI()
  const getAudienceList = async payload => {
    try {
      const response = await api.post(`${audiencesUrl}/search`, payload)
      setAudiencesListRow(response?.data?.data?.audiences ?? [])
      setRowCount(response?.data?.data?.total ?? 0)
    } catch {}
  }

  const getAudience = async (id, action) => {
    setSelectedAudienceID(id)
    const queryParams = {
      action: action
    }

    try {
      const response = await api.get(`${audiencesUrl}/${id}`, {
        params: queryParams
      })

      setSelectedAudience(response?.data?.data)
    } catch {}
  }

  const addAudienceData = async audienceData => {
    try {
      const response = await api.post(audiencesUrl, audienceData)
      showSuccess('Audience added successfully!')
      setSelectedTags([])
      router.push(`/customer-data-platform/all-audience`)

      return response?.status
    } catch {}
  }

  const editAudienceData = async (id, audienceData) => {
    try {
      await api.put(`${audiencesUrl}/${id}`, audienceData)
      showSuccess('Audience edited successfully!')
      setSelectedTags([])
      router.push(`/customer-data-platform/all-audience`)
    } catch {}
  }

  const getAllTags = async (query = '') => {
    try {
      const url = `${getTags}?query=${encodeURIComponent(query)}`
      const response = await api.get(url)
      setTagsData(response?.data?.Data || [])
    } catch {
      // ignore
    }
  }

  const deleteAudience = async id => {
    try {
      await api.delete(`${audiencesUrl}/${id}`)
      getAudienceList({})
    } catch {}
  }

  const audienceBulkAction = async (selectedIDList, bulkAction) => {
    try {
      const inputData = {
        audienceIds: selectedIDList,
        action: bulkAction
      }
      const response = await api.put(`${audiencesUrl}/status`, inputData)
      if (response?.status === 200 || response?.status === 204) {
        getAudienceList({})
      }
    } catch {}
  }

  const startImport = async import_id => {
    try {
      const response = await api.post(`${audienceImportUrl}/${import_id}`, {
        create_segment: createSegment
      })

      if (createSegment) {
        const fileName = files?.[0]?.name ? files[0].name.replace(/\.[^/.]+$/, '') : 'imported_segment'
        showSuccess(
          `The audience import has been successfully completed. A segment '${fileName}' was also created from the import tags.`
        )
      } else {
        showSuccess('Import started successfully!...Please wait a few seconds!...')
      }

      if (response?.status === 200) {
        setTimeout(() => {
          router.push(`/customer-data-platform/all-audience`)
        }, 3000)
      }
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to start import. Please try again.')
    }
  }

  const updateImportData = async data => {
    try {
      const response = await api.put(audienceImportUrl, data)
      setPreviewData(response?.data?.data?.Data)
      showSuccess('Audience columns mapped successfully!')

      return null
    } catch {}
  }

  const uploadFilesData = async formData => {
    try {
      const response = await api.post(audienceImportUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const mappedColumnsCount = response?.data?.data?.Data?.mapped_columns?.length

      if (mappedColumnsCount > MAX_IMPORT_COLUMNS) {
        showError(`File should not contain more than ${MAX_IMPORT_COLUMNS} columns`)

        return 'Too many columns'
      }

      setRawFileHeadersData(response?.data?.data?.Data)
      const uploadedSystemColumns = response?.data?.data?.Data?.system_columns
      setPiiColumns(updatedPiiColumns(uploadedSystemColumns))
      setSystemColumns(uploadedSystemColumns)
      showSuccess('File Uploaded successfully!')

      return null
    } catch {}
  }

  const fetchAudienceEvents = async universal_profile_id => {
    try {
      const queryParams = {
        page: (page + 1).toString(),
        pagesize: rowsPerPage.toString(),
        query: eventSearchTerm
      }

      const response = await api.get(`${audiencesUrl}/${universal_profile_id}/events`, {
        params: queryParams
      })
      const dataArray = response?.data?.data

      setEventRowCount(dataArray[dataArray?.length - 1])
      setSelectedAudienceEvents(dataArray?.slice(0, -1))
    } catch {}
  }

  const highlightText = (text, highlightSearchTerm) => {
    if (!highlightSearchTerm.trim()) {
      return text
    }
    const parts = text?.split(new RegExp(`(${highlightSearchTerm})`, 'gi'))

    return parts.map((part, index) =>
      part.toLowerCase() === highlightSearchTerm.toLowerCase() ? (
        <Box component='span' key={`${part}-${index}`} sx={{ backgroundColor: 'yellow' }}>
          {part}
        </Box>
      ) : (
        part
      )
    )
  }

  const filterDataTypeIcon = data_type => {
    if (data_type === 'string') {
      return <img src={`/images/text.png`} alt='text' width={40} height={30} loading='lazy' />
    } else if (data_type === 'int' || data_type === 'float') {
      return <img src={`/images/number.png`} alt='number' width={35} height={25} loading='lazy' />
    } else if (data_type === 'null') {
      return null
    } else if (data_type === 'date') {
      return <img src={`/images/date.png`} alt='date' width={40} height={30} loading='lazy' />
    } else if (data_type === 'jsonb') {
      return <img src={`/images/json.png`} alt='jsonb' width={40} height={30} loading='lazy' />
    } else {
      return null
    }
  }

  const formatToDateString = dateString => {
    if (!dateString || dateString === '""') {
      return ''
    } else {
      const cleanedDateString = dateString.replace(/(^["'])|(["']$)/g, '')
      const date = new Date(cleanedDateString)

      if (isNaN(date.getTime())) {
        return ''
      }

      const year = date.getFullYear()

      const month = String(date.getMonth() + 1).padStart(2, '0')

      const day = String(date.getDate()).padStart(2, '0')

      return `${month}/${day}/${year}`
    }
  }

  const normalizeField = field => {
    if (field === `""` || field === '' || field === '****') {
      return ''
    } else {
      return field
    }
  }

  const updatedPiiColumns = columnsInput => {
    const filteredPiiColumns = columnsInput?.filter(item => item.is_pii === true).map(item => item.system_column)

    return filteredPiiColumns
  }

  function formatDateTime(isoString) {
    const date = new Date(isoString)

    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true }
    const time = new Intl.DateTimeFormat('en-US', timeOptions).format(date)

    const dateOptions = { month: 'short', day: 'numeric', year: 'numeric' }
    const fullDate = new Intl.DateTimeFormat('en-US', dateOptions).format(date)

    return { time, fullDate }
  }

  const getAudienceFilterList = async () => {
    try {
      const response = await api.get(`${filterUrl}/audiences`)

      const filters = response?.data?.data?.filters || []
      const otherFilters = response?.data?.data?.otherFilters || []

      const tagsFilter = filters.find(f => f.key === 'tags')
      setHasTagFilter(!!tagsFilter)
      setAudienceFilterList(tagsFilter ? tagsFilter.options.map(o => o.value) : [])

      setAudienceOtherFilterList(otherFilters)

      const initialSelectedFilters = {}
      otherFilters.forEach(f => {
        initialSelectedFilters[f.key] = []
      })

      return initialSelectedFilters
    } catch {
      setAudienceFilterList([])
      setAudienceOtherFilterList([])
      return {}
    }
  }

  const statusIcon = iconName => {
    if (iconName === 'sms') {
      return <Icon color='#1c9ae9' icon='mdi:message-text-outline' fontSize={20} />
    }
    if (iconName === 'whatsapp') {
      return <Icon color='#228B22' icon='mdi:whatsapp' fontSize={20} />
    }
    if (iconName === 'email') {
      return <Icon color='#B87333' icon='mdi:email-outline' fontSize={20} />
    }
    if (iconName === 'rcs') {
      return <Icon color='#0066CC' icon='mdi:message-badge-outline' fontSize={20} />
    }

    return null
  }

  const values = useMemo(() => ({
    eventRowCount,
    setEventRowCount,
    eventSearchTerm,
    setEventSearchTerm,
    selectedAudienceEvents,
    setSelectedAudienceEvents,
    csvData,
    setCsvData,
    importMethod,
    setImportMethod,
    mandatoryColumnsToImport,
    isPiiChanged,
    setIsPiiChanged,
    openConfirmPii,
    setOpenConfirmPii,
    piiColumns,
    setPiiColumns,
    deleteId,
    setDeleteId,
    deleteAudienceName,
    setDeleteAudienceName,
    openDelete,
    setOpenDelete,
    selectedColumns,
    setSelectedColumns,
    files,
    setFiles,
    mappedColumns,
    setMappedColumns,
    systemColumns,
    setSystemColumns,
    selectedTags,
    setSelectedTags,
    autoFileTags,
    setAutoFileTags,
    tagsData,
    setTagsData,
    getAllTags,
    rawFileHeadersData,
    setRawFileHeadersData,
    updatedRawFileHeadersData,
    setUpdatedRawFileHeadersData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    rows,
    setRows,
    selectedRows,
    setSelectedRows,
    rowCount,
    setRowCount,
    sortOrder,
    setSortOrder,
    sortOrderBy,
    setSortOrderBy,
    previewData,
    setPreviewData,
    audiencesListRow,
    setAudiencesListRow,
    selectedAudience,
    filterDataTypeIcon,
    setSelectedAudience,
    selectedAudienceID,
    setSelectedAudienceID,
    systemColumnList,
    setSystemColumnList,
    columnStatusList,
    setColumnStatusList,
    dataStatus,
    setDataStatus,
    maskField,
    setMaskField,
    isUniqueColumnExists,
    setIsUniqueColumnExists,
    schema,
    defaultValues,
    normalizeField,
    formatToDateString: formatToDateString,
    highlightText: highlightText,
    formatDateTime: formatDateTime,
    statusIcon: statusIcon,
    getAudienceList: getAudienceList,
    getAudience: getAudience,
    addAudienceData: addAudienceData,
    editAudienceData: editAudienceData,
    deleteAudience: deleteAudience,
    audienceBulkAction: audienceBulkAction,
    uploadFilesData: uploadFilesData,
    updateImportData: updateImportData,
    startImport: startImport,
    fetchAudienceEvents: fetchAudienceEvents,
    getAudienceFilterList: getAudienceFilterList,
    audienceFilterList,
    setAudienceFilterList,
    audienceOtherFilterList,
    setAudienceOtherFilterList,
    hasTagFilter,
    setHasTagFilter,
    isNewFileUploaded,
    setIsNewFileUploaded,
    createSegment,
    setCreateSegment
  }))

  return <AudienceContext.Provider value={values}>{children}</AudienceContext.Provider>
}
AudienceProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { AudienceContext, AudienceProvider, defaultProvider }
